/**
 * Iter 149.5 — Shared pay-display helper.
 *
 * Renders a single short badge string from a job's pay_mode + salary fields.
 * Applies everywhere applicants see a job card (home feed, LiveJobsTicker,
 * JobMatches, JobDetail header). Kept in one file so a future change to
 * currency locale or DOE copy is a one-line edit.
 *
 * Returned string is short-form ("$90k–$130k", "Starting at $65k+", "DOE")
 * so it fits in a rounded pill next to Location / Remote / Employment-type
 * chips. Returns null when the job has no discoverable pay signal AND no
 * pay_mode field — cards should hide the pill entirely rather than render
 * an empty one.
 */

const _sym = (currency) => (currency && currency !== "USD" ? currency + " " : "$");
const _short = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k` : String(n));
const _suffix = (period) => {
    const p = (period || "YEAR").toUpperCase();
    if (p === "HOUR") return "/hr";
    if (p === "DAY") return "/day";
    if (p === "MONTH") return "/mo";
    return "";  // YEAR is the default — no suffix
};

export function formatJobPay(job) {
    if (!job) return null;
    const mode = job.pay_mode;
    const min = Number(job.salary_min || 0);
    const max = Number(job.salary_max || 0);
    const currency = job.salary_currency || "USD";
    const sym = _sym(currency);
    const suffix = _suffix(job.salary_period);

    // Explicit DOE — no numeric even attempted.
    if (mode === "doe") return "DOE";

    // Explicit starting_at — one anchor number.
    if (mode === "starting_at" && min > 0) return `Starting at ${sym}${_short(min)}${suffix}`;

    // Explicit range or legacy (no mode + both numbers): render the range.
    if (min > 0 && max > 0) {
        // Collapse "$100k–$100k" to just "$100k".
        if (min === max) return `${sym}${_short(min)}${suffix}`;
        return `${sym}${_short(min)}–${sym}${_short(max)}${suffix}`;
    }
    // Legacy fallback — no mode, just a free-text `salary` string was set.
    if (job.salary) return String(job.salary);
    // Single-number legacy row (only min OR only max).
    if (min > 0) return `${sym}${_short(min)}${suffix}`;
    if (max > 0) return `Up to ${sym}${_short(max)}${suffix}`;
    return null;
}

/**
 * Return the semantic tone for the pay badge — used by callers that want
 * a different colour for DOE vs concrete numbers.
 *   "concrete" → green pill (range / starting at / any numeric)
 *   "doe"      → amber pill (compliance signal, less certain)
 *   "none"     → hide the pill entirely
 */
export function payToneForJob(job) {
    if (!job) return "none";
    if (job.pay_mode === "doe") return "doe";
    const min = Number(job.salary_min || 0);
    const max = Number(job.salary_max || 0);
    if (min > 0 || max > 0 || job.salary) return "concrete";
    return "none";
}
